import React, { useEffect, useState } from 'react';
import { landlordAPI } from '../../lib/api';
import { formatCurrency, getStatusColor, getInitials } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { toast } from 'sonner';
import { DoorOpen, Plus, Pencil, Trash2, Building2, User, Loader2, Layers, CheckCircle, Search, X } from 'lucide-react';

export default function LandlordUnits() {
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterProperty, setFilterProperty] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    property_id: '',
    unit_number: '',
    floor: '',
    capacity: 1,
    monthly_rent: '',
    amenities: [],
  });
  const [bulkFormData, setBulkFormData] = useState({
    property_id: '',
    prefix: 'Room',
    start_number: 1,
    count: 5,
    floor: '',
    capacity: 1,
    monthly_rent: '',
    amenities: [],
    number_format: 'numeric',
  });
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [unitsRes, propsRes] = await Promise.all([
        landlordAPI.getUnits(),
        landlordAPI.getProperties(),
      ]);
      setUnits(unitsRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredUnits = units.filter(u => {
    if (filterProperty !== 'all' && u.property_id !== filterProperty) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchUnit = (u.unit_number || '').toLowerCase().includes(q);
      const matchProp = (u.property_name || '').toLowerCase().includes(q);
      const matchStatus = (u.status || '').toLowerCase().includes(q);
      const matchTenant = u.current_tenants?.some(t => (t.name || '').toLowerCase().includes(q));
      const matchFloor = (u.floor || '').toLowerCase().includes(q);
      if (!matchUnit && !matchProp && !matchStatus && !matchTenant && !matchFloor) return false;
    }
    return true;
  });

  const handleOpenDialog = (unit = null) => {
    if (unit) {
      setEditingUnit(unit);
      setFormData({
        property_id: unit.property_id,
        unit_number: unit.unit_number,
        floor: unit.floor || '',
        capacity: unit.capacity,
        monthly_rent: unit.monthly_rent.toString(),
        amenities: unit.amenities || [],
      });
    } else {
      setEditingUnit(null);
      setFormData({
        property_id: filterProperty !== 'all' ? filterProperty : '',
        unit_number: '',
        floor: '',
        capacity: 1,
        monthly_rent: '',
        amenities: [],
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.property_id || !formData.unit_number || !formData.monthly_rent) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        monthly_rent: parseFloat(formData.monthly_rent),
        capacity: parseInt(formData.capacity),
      };

      if (editingUnit) {
        await landlordAPI.updateUnit(editingUnit.unit_id, data);
        toast.success('Unit updated successfully');
      } else {
        await landlordAPI.createUnit(data);
        toast.success('Unit created successfully');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save unit');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (unitId) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;

    try {
      await landlordAPI.deleteUnit(unitId);
      toast.success('Unit deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete unit');
    }
  };

  const handleBulkCreate = async () => {
    if (!bulkFormData.property_id || !bulkFormData.monthly_rent || !bulkFormData.count) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    setBulkResult(null);
    try {
      const data = {
        ...bulkFormData,
        monthly_rent: parseFloat(bulkFormData.monthly_rent),
        capacity: parseInt(bulkFormData.capacity),
        count: parseInt(bulkFormData.count),
        start_number: parseInt(bulkFormData.start_number),
      };

      const response = await landlordAPI.createUnitsBulk(data);
      setBulkResult(response.data);
      toast.success(`Successfully created ${response.data.count} units!`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create units');
    } finally {
      setSaving(false);
    }
  };

  const resetBulkDialog = () => {
    setBulkDialogOpen(false);
    setBulkResult(null);
    setBulkFormData({
      property_id: '',
      prefix: 'Room',
      start_number: 1,
      count: 5,
      floor: '',
      capacity: 1,
      monthly_rent: '',
      amenities: [],
      number_format: 'numeric',
    });
  };

  // Generate preview of unit names
  const getBulkPreview = () => {
    const { prefix, start_number, count, number_format } = bulkFormData;
    const previews = [];
    for (let i = 0; i < Math.min(count, 5); i++) {
      const num = start_number + i;
      let unitNum;
      if (number_format === 'padded') {
        unitNum = `${prefix} ${String(num).padStart(3, '0')}`;
      } else if (number_format === 'alphanumeric') {
        let letter = '';
        let n = num;
        while (n > 0) {
          n -= 1;
          letter = String.fromCharCode(65 + (n % 26)) + letter;
          n = Math.floor(n / 26);
        }
        unitNum = `${prefix} ${letter}`;
      } else {
        unitNum = `${prefix} ${num}`;
      }
      previews.push(unitNum);
    }
    if (count > 5) previews.push('...');
    return previews;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landlord-units-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Units</h1>
          <p className="text-muted-foreground mt-1">Manage your dormitory rooms</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search units..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
              data-testid="unit-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={filterProperty} onValueChange={setFilterProperty}>
            <SelectTrigger className="w-[180px]" data-testid="unit-filter-property">
              <SelectValue placeholder="Filter by property" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map((prop) => (
                <SelectItem key={prop.property_id} value={prop.property_id}>
                  {prop.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={() => setBulkDialogOpen(true)} 
            data-testid="bulk-create-btn"
            disabled={properties.length === 0}
          >
            <Layers className="w-4 h-4 mr-2" />
            Bulk Create
          </Button>
          <Button onClick={() => handleOpenDialog()} className="bg-[#C85A42] hover:bg-[#B04A35]" data-testid="add-unit-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Unit
          </Button>
        </div>
      </div>

      {properties.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg mb-2">No properties yet</h3>
            <p className="text-muted-foreground">Create a property first before adding units</p>
          </CardContent>
        </Card>
      ) : filteredUnits.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <DoorOpen className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg mb-2">No units yet</h3>
            <p className="text-muted-foreground mb-4">Add rooms to your property</p>
            <Button onClick={() => handleOpenDialog()} className="bg-[#C85A42] hover:bg-[#B04A35]">
              <Plus className="w-4 h-4 mr-2" />
              Add Unit
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUnits.map((unit, index) => (
            <Card key={unit.unit_id} className="card-hover animate-fade-in" style={{ animationDelay: `${index * 0.03}s` }}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">Room {unit.unit_number}</h3>
                    <p className="text-sm text-muted-foreground">{unit.property_name}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={getStatusColor(unit.status)}>
                      {unit.status === 'partially_occupied' ? 'Partial' : unit.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {unit.current_occupants || 0}/{unit.capacity} occupied
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm mb-4">
                  {unit.floor && <p><span className="text-muted-foreground">Floor:</span> {unit.floor}</p>}
                  <p><span className="text-muted-foreground">Capacity:</span> {unit.capacity} person(s)</p>
                  <p className="text-lg font-semibold text-[#C85A42]">{formatCurrency(unit.monthly_rent)}/mo</p>
                </div>

                {unit.current_tenants && unit.current_tenants.length > 0 && (
                  <div className="space-y-2 mb-3">
                    <p className="text-xs text-muted-foreground font-medium">Current Tenants:</p>
                    {unit.current_tenants.map((tenant) => (
                      <div key={tenant.user_id} className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                        <Avatar className="w-7 h-7">
                          <AvatarFallback className="bg-[#6B8E6B] text-white text-xs">
                            {getInitials(tenant.name)}
                          </AvatarFallback>
                        </Avatar>
                        <p className="text-sm font-medium truncate">{tenant.name}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-1 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(unit)} data-testid={`edit-unit-${unit.unit_id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(unit.unit_id)} disabled={unit.current_occupants > 0 || unit.status === 'occupied'} data-testid={`delete-unit-${unit.unit_id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Unit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUnit ? 'Edit Unit' : 'Add New Unit'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Property *</Label>
              <Select
                value={formData.property_id}
                onValueChange={(v) => setFormData({ ...formData, property_id: v })}
                disabled={!!editingUnit}
              >
                <SelectTrigger data-testid="unit-property-select">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.property_id} value={prop.property_id}>
                      {prop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="unit_number">Unit Number *</Label>
                <Input
                  id="unit_number"
                  value={formData.unit_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit_number: e.target.value }))}
                  placeholder="e.g., 101"
                  data-testid="unit-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="floor">Floor</Label>
                <Input
                  id="floor"
                  value={formData.floor}
                  onChange={(e) => setFormData(prev => ({ ...prev, floor: e.target.value }))}
                  placeholder="e.g., Ground"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="capacity">Capacity *</Label>
                <Input
                  id="capacity"
                  type="text"
                  min="1"
                  value={formData.capacity}
                  onChange={(e) => setFormData(prev => ({ ...prev, capacity: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="monthly_rent">Monthly Rent (₱) *</Label>
                <Input
                  id="monthly_rent"
                  type="text"
                  value={formData.monthly_rent}
                  onChange={(e) => setFormData(prev => ({ ...prev, monthly_rent: e.target.value }))}
                  placeholder="5000"
                  data-testid="unit-rent-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#C85A42] hover:bg-[#B04A35]" data-testid="save-unit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingUnit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Create Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => !open && resetBulkDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Bulk Create Units
            </DialogTitle>
            <DialogDescription>
              Create multiple units at once with a naming pattern
            </DialogDescription>
          </DialogHeader>
          
          {bulkResult ? (
            <div className="space-y-4">
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Units Created Successfully!</h3>
                <p className="text-muted-foreground">{bulkResult.count} units have been added to your property</p>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4 max-h-40 overflow-y-auto">
                <p className="text-sm font-medium mb-2">Created Units:</p>
                <div className="flex flex-wrap gap-2">
                  {bulkResult.units.map((unit) => (
                    <Badge key={unit.unit_id} variant="secondary">
                      {unit.unit_number}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <DialogFooter>
                <Button onClick={resetBulkDialog} className="w-full bg-[#C85A42] hover:bg-[#B04A35]">
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Property *</Label>
                  <Select 
                    value={bulkFormData.property_id} 
                    onValueChange={(val) => setBulkFormData({ ...bulkFormData, property_id: val })}
                  >
                    <SelectTrigger data-testid="bulk-property-select">
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((prop) => (
                        <SelectItem key={prop.property_id} value={prop.property_id}>
                          {prop.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Prefix</Label>
                    <Input
                      value={bulkFormData.prefix}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, prefix: e.target.value })}
                      placeholder="Room"
                      data-testid="bulk-prefix-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Start #</Label>
                    <Input
                      type="text"
                      min="1"
                      value={bulkFormData.start_number}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, start_number: parseInt(e.target.value) || 1 })}
                      data-testid="bulk-start-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Count *</Label>
                    <Input
                      type="text"
                      min="1"
                      max="100"
                      value={bulkFormData.count}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, count: parseInt(e.target.value) || 1 })}
                      data-testid="bulk-count-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Numbering Format</Label>
                  <Select 
                    value={bulkFormData.number_format} 
                    onValueChange={(val) => setBulkFormData({ ...bulkFormData, number_format: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="numeric">Numeric (1, 2, 3...)</SelectItem>
                      <SelectItem value="padded">Padded (001, 002, 003...)</SelectItem>
                      <SelectItem value="alphanumeric">Letters (A, B, C...)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Preview */}
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Preview:</p>
                  <div className="flex flex-wrap gap-2">
                    {getBulkPreview().map((name) => (
                      <Badge key={name} variant="outline" className="text-xs">
                        {name}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Floor</Label>
                    <Input
                      value={bulkFormData.floor}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, floor: e.target.value })}
                      placeholder="1st Floor"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Capacity</Label>
                    <Input
                      type="text"
                      min="1"
                      value={bulkFormData.capacity}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, capacity: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Monthly Rent (₱) *</Label>
                  <Input
                    type="text"
                    value={bulkFormData.monthly_rent}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, monthly_rent: e.target.value })}
                    placeholder="5000"
                    data-testid="bulk-rent-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={resetBulkDialog}>Cancel</Button>
                <Button 
                  onClick={handleBulkCreate} 
                  disabled={saving || !bulkFormData.property_id || !bulkFormData.monthly_rent}
                  className="bg-[#C85A42] hover:bg-[#B04A35]"
                  data-testid="bulk-create-submit-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Layers className="w-4 h-4 mr-2" />}
                  Create {bulkFormData.count} Units
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
